import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, map, tap } from 'rxjs';
import { ExternalLinkWarningDialogComponent } from '../../../shared/components/external-link-warning-dialog/external-link-warning-dialog.component';
import { IdleWarningDialogComponent } from '../../../shared/components/idle-warning-dialog/idle-warning-dialog.component';
import { IosChromeWarningDialogComponent } from '../../../shared/components/ios-chrome-warning-dialog/ios-chrome-warning-dialog.component';
import { closeOnEscape, openUnsavedChangesDialog } from '../../../shared/functions/dialog.functions';
import { AppInsightsFeatureType } from '../../models/app-insights-feature-type';
import { AppInsightsService } from '../../services/app-insights/app-insights.service';
import { IdleTimeoutService } from '../../services/idle-timeout/idle-timeout.service';
import {
  closeIdleWarningDialog,
  confirmCancel,
  navigateToExternalPage,
  openIdleWarningDialog,
  openIosChromeWarning,
} from '../actions/app.actions';
import { selectExternalUrls } from '../selectors/app.selector';

@Injectable()
export class AppDialogEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private appInsightsService = inject(AppInsightsService);
  private idleTimeoutService = inject(IdleTimeoutService);

  openExternalLinkWarningDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(navigateToExternalPage),
        concatLatestFrom(() => [this.store.select(selectExternalUrls)]),
        exhaustMap(([action, externalUrls]) => {
          const dialogRef = this.dialog.open(ExternalLinkWarningDialogComponent, {
            id: 'external-link-warning-dialog',
            disableClose: true,
            autoFocus: false,
            position: { top: '150px' },
            width: '400px',
            minHeight: '200px',
            panelClass: 'dialog',
          });
          closeOnEscape(dialogRef);

          return dialogRef.afterClosed().pipe(
            map(result => {
              if (result) {
                const externalUrl = externalUrls?.find(f => f.externalUrlTypeId === action.externalUrlTypeId);
                if (externalUrl !== undefined) {
                  this.appInsightsService.trackEvent('Navigation.ExternalLink.Clicked', {
                    feature: AppInsightsFeatureType.NAVIGATION,
                    externalUrl: externalUrl.url,
                  });
                  window.open(externalUrl.url, '_blank');
                }
              }
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  openUnsavedChangesDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(confirmCancel),
        tap(action => {
          openUnsavedChangesDialog(this.dialog, action.continueAction);
        })
      );
    },
    { dispatch: false }
  );

  openIdleWarningDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openIdleWarningDialog),
        exhaustMap(() => {
          const dialogRef = this.dialog.open(IdleWarningDialogComponent, {
            id: 'idle-warning-dialog',
            disableClose: true,
            autoFocus: false,
            position: { top: '150px' },
            width: '350px',
            minHeight: '200px',
            panelClass: 'dialog',
          });
          // Escape resolves as "stay logged in" (matches the dialog's OK button), so dismissing
          // the warning extends the session rather than silently leaving it on the logout path.
          closeOnEscape(dialogRef, { closeResult: true });

          return dialogRef.afterClosed().pipe(
            map(result => {
              if (result) {
                this.idleTimeoutService.initiateReset();
              }
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  closeIdleWarningDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(closeIdleWarningDialog),
        tap(() => {
          this.dialog.closeAll();
        })
      );
    },
    { dispatch: false }
  );

  openIosChromeWarningDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openIosChromeWarning),
        tap(() => {
          const dialogRef = this.dialog.open(IosChromeWarningDialogComponent, {
            id: 'ios-chrome-warning-dialog',
            disableClose: true,
            autoFocus: false,
            position: { top: '150px' },
            width: '500px',
            minHeight: '250px',
            panelClass: 'dialog',
          });
          closeOnEscape(dialogRef);
        })
      );
    },
    { dispatch: false }
  );
}
